import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Plus, Palette, Edit3 } from 'lucide-react';
import { getSubjects, saveSubjects } from '../utils/storage';

const SUBJECT_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280', // Gray
];

const AddSubject = () => {
  const [formData, setFormData] = useState({
    name: '',
    color: SUBJECT_COLORS[0]
  });
  const [errors, setErrors] = useState({});
  const [editingSubject, setEditingSubject] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we're editing a subject
    if (location.state?.subject) {
      const subject = location.state.subject;
      setEditingSubject(subject);
      setFormData({
        name: subject.name,
        color: subject.color
      });
    }
  }, [location.state]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Subject name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Subject name must be at least 2 characters';
    } else {
      // Check for duplicate names
      const existingSubjects = getSubjects();
      const duplicate = existingSubjects.find(
        subj => subj.name.toLowerCase() === formData.name.trim().toLowerCase()
      );
      if (duplicate && (!editingSubject || duplicate.id !== editingSubject.id)) {
        newErrors.name = 'A subject with this name already exists';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const subjects = getSubjects();

    if (editingSubject) {
      // Update existing subject
      const updatedSubjects = subjects.map(subj =>
        subj.id === editingSubject.id
          ? { ...subj, name: formData.name.trim(), color: formData.color }
          : subj
      );
      saveSubjects(updatedSubjects);
    } else {
      // Create new subject
      const newSubject = {
        id: Date.now().toString(),
        name: formData.name.trim(),
        color: formData.color
      };
      subjects.push(newSubject);
      saveSubjects(subjects);
    }

    navigate('/subjects');
  };

  const isFormValid = formData.name.trim().length >= 2;

  return (
    <div className="container">
      <div className="form-header">
        <h1>{editingSubject ? 'Edit Subject' : 'Add New Subject'}</h1>
        <p className="form-subtitle">
          {editingSubject ? 'Update the subject details' : 'Create a subject to organize your tasks and track progress'}
        </p>
      </div>

      <div className="form-card">
        <div className="form-group">
          <label htmlFor="subject-name" className="form-label">
            Subject Name *
          </label>
          <div className="input-wrapper">
            <BookOpen size={16} className="input-icon" />
            <input
              id="subject-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Mathematics, History, Science"
              className={`input ${errors.name ? 'error' : ''}`}
              maxLength={50}
            />
          </div>
          {errors.name && (
            <div className="error-message">
              <Palette size={16} />
              {errors.name}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">
            Subject Color
          </label>
          <div className="color-picker">
            {SUBJECT_COLORS.map(color => (
              <button
                key={color}
                type="button"
                className={`color-option ${formData.color === color ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => handleInputChange('color', color)}
                aria-label={`Select color ${color}`}
              >
                {formData.color === color && <span className="color-check">✓</span>}
              </button>
            ))}
          </div>
          <p className="color-hint">
            Choose a color to visually distinguish this subject in your tasks
          </p>
        </div>

        <div className="form-actions">
          <button
            onClick={() => navigate('/subjects')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="save-button"
            disabled={!isFormValid}
          >
            {editingSubject ? <Edit3 size={20} /> : <Plus size={20} />}
            {editingSubject ? 'Update Subject' : 'Create Subject'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSubject;
